import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { LifecycleHighlightBox } from './LifecycleHighlightBox';

/**
 * Plan-status card rendered on Start-Page (Batch 7) and Account-Page
 * (Batch 9A — A-01 + A-02). Component is intentionally state-agnostic:
 * every piece of visible copy is a caller-supplied prop, so the same
 * shell handles Credits-states (Batch 7) and the 3 lifecycle states
 * (Pending-Cancel / Past-Due / Pending-Plan-Change, Batch 9A) with no
 * API break.
 *
 * The route layer derives props from the relevant hook:
 *   - Credits states          → useEntitlement() + derivePlanStatusProps
 *   - Lifecycle states        → useSubscriptionState() + deriveLifecyclePlanStatusProps
 *
 * Briefing Chapter 6 line 794 specifies 5 lifecycle states share this
 * card; the 3 remaining states (Pending-Cancel, Past-Due, Pending-Plan-
 * Change) ship in Batch 9A. Do NOT rename to `CreditsStatusCard` — the
 * shell is reused across pages with different data feeds.
 *
 * "Plan-Wechsel" naming convention applies to user-facing strings per
 * Batch 9A FB-7. The DB field name `pendingDowngrade*` stays — only
 * user-visible copy uses the neutral "Plan-Wechsel" wording.
 */

// ── cva variants ───────────────────────────────────────────────

const planStatusCardVariants = cva(
  'flex flex-col items-start justify-center gap-4 rounded-lg p-4',
  {
    variants: {
      tone: {
        neutral: 'bg-dream-background',
        warning: 'bg-dream-white',
        error: 'bg-dream-white',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

const ctaVariants = cva(
  'flex w-full items-center justify-center overflow-hidden rounded-lg px-4 py-[14px] text-xs font-semibold leading-3 transition-colors',
  {
    variants: {
      tone: {
        neutral: 'bg-dream-gold text-dream-navy hover:bg-dream-gold/90',
        warning: 'bg-success text-dream-white hover:bg-success/90',
        error: 'bg-error text-dream-white hover:bg-error/90',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

// ── Component ──────────────────────────────────────────────────

type Tone = NonNullable<VariantProps<typeof planStatusCardVariants>['tone']>;

export interface PlanStatusCardProps {
  headline: string;
  subtitleText: string;
  /**
   * PWA #71 — optional second subtitle line surfacing the user's
   * non-subscription credit reserve when the subscription is exhausted
   * (mixed-state). Rendered below subtitleText on `tone='neutral'` only.
   * `derivePlanStatusProps:subscriptionLabel` populates this via
   * `pickReserveLine(entitlement)`. Lifecycle tones (warning / error)
   * suppress it — those states have their own concerns and the
   * highlight box already owns the subtitle slot.
   */
  reserveLineText?: string;
  /**
   * Bug 2C — optional usage/quota line surfaced in the lifecycle tones
   * (warning / error). On Start + Account a scheduled plan change flips
   * the card to the lifecycle banner via `deriveLifecyclePlanStatusProps`,
   * which previously REPLACED the credits-derived quota line entirely —
   * so the user lost sight of "Noch X von Y verfügbar" while a change is
   * pending. The Abo page (SubscriptionSummaryCard) shows both; this prop
   * restores parity on the PlanStatusCard surfaces. Rendered ABOVE the
   * highlight box (matching the Abo page's counter-row-then-banner order)
   * and only when provided — `deriveLifecyclePlanStatusProps` populates it
   * for the pending-plan-change branch only.
   */
  quotaLineText?: string;
  ctaLabel: string;
  onCtaClick: () => void;
  /**
   * Visual treatment. Default `'neutral'` preserves Batch 7 behavior
   * exactly (yellow-light card body, gold CTA, plain-text subtitle).
   *   - `'warning'` (Pending-Cancel / Pending-Plan-Change): white card
   *     body, subtitle wrapped in a yellow highlight box, success
   *     (green) CTA.
   *   - `'error'` (Past-Due): white card body, subtitle wrapped in a
   *     red highlight box, error (red) CTA.
   *
   * Mockup source: dreamable_ch2_account_states_modals.html. Token
   * mapping per Batch 9A FB-2: warning uses dream-gold/15 + warning-text;
   * error uses error/10 + error-text (new --color-error-text token).
   */
  tone?: Tone;
  className?: string;
  testId?: string;
}

export function PlanStatusCard({
  headline,
  subtitleText,
  reserveLineText,
  quotaLineText,
  ctaLabel,
  onCtaClick,
  tone = 'neutral',
  className,
  testId,
}: PlanStatusCardProps) {
  return (
    <div className={cn(planStatusCardVariants({ tone }), className)} data-testid={testId}>
      <div className="flex w-full flex-col items-start gap-3">
        <p className="text-sm font-semibold leading-[14px] text-dream-navy">{headline}</p>
        {tone === 'neutral' ? (
          <div className="flex w-full flex-col gap-1">
            <p
              className="w-full text-xs leading-5 text-dream-navy opacity-50"
              data-testid={testId ? `${testId}-subtitle` : undefined}
            >
              {subtitleText}
            </p>
            {reserveLineText && (
              <p
                className="w-full text-xs leading-5 text-dream-navy opacity-50"
                data-testid={testId ? `${testId}-reserve-line` : undefined}
              >
                {reserveLineText}
              </p>
            )}
          </div>
        ) : (
          // testId mirrors the Commit 2 inline shape so existing
          // PlanStatusCard tests (which query
          // `plan-status-highlight-{tone}`) keep passing unchanged.
          // Lifecycle tones suppress reserveLineText — those states
          // own the subtitle slot via the highlight box and have
          // their own concerns (PWA #71 is orthogonal to lifecycle).
          // Bug 2C: when quotaLineText is provided (pending-plan-change
          // only) it renders above the banner, mirroring the Abo page's
          // counter-row-then-banner order so the quota stays visible.
          <div className="flex w-full flex-col gap-3">
            {quotaLineText && (
              <p
                className="w-full text-xs leading-5 text-dream-navy opacity-50"
                data-testid={testId ? `${testId}-quota-line` : undefined}
              >
                {quotaLineText}
              </p>
            )}
            <LifecycleHighlightBox tone={tone} testId={`plan-status-highlight-${tone}`}>
              {subtitleText}
            </LifecycleHighlightBox>
          </div>
        )}
      </div>
      <button type="button" onClick={onCtaClick} className={ctaVariants({ tone })}>
        {ctaLabel}
      </button>
    </div>
  );
}

// HighlightBox extracted to `patterns/LifecycleHighlightBox.tsx`
// (Batch 9A Commit 3a) when SubscriptionRoute became a 2nd consumer.
// Visual treatment is preserved 1:1; testId routing matches the prior
// inline shape (`plan-status-highlight-warning` / `…-error`) so existing
// tests query the same element.
